import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as teamsJs from '@microsoft/teams-js';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';

/**
 * Clé distincte pour indiquer que la langue a été choisie EXPLICITEMENT par l'utilisateur
 * (via LanguageSelector), et non détectée automatiquement depuis le navigateur.
 */
export const USER_LANGUAGE_CHOICE_KEY = 'requestateam_language_user_choice';

const TEAMS_TO_I18N_MAP: Record<string, string> = {
  'de': 'de', 'de-DE': 'de', 'de-AT': 'de', 'de-CH': 'de',
  'en': 'en', 'en-US': 'en', 'en-GB': 'en', 'en-AU': 'en',
  'es': 'es', 'es-ES': 'es', 'es-MX': 'es', 'es-419': 'es',
  'fr': 'fr', 'fr-FR': 'fr', 'fr-BE': 'fr', 'fr-CA': 'fr', 'fr-CH': 'fr',
  'it': 'it', 'it-IT': 'it', 'it-CH': 'it',
  'zh-CN': 'zh-CN', 'zh-Hans': 'zh-CN', 'zh-Hans-CN': 'zh-CN',
};

/**
 * Hook to detect and sync Teams locale with i18next
 * Priority: localStorage override > Teams context > browser navigator > fallback to English
 */
export function useTeamsLocale(teamsContext: teamsJs.app.Context | null) {
  const { i18n } = useTranslation();

  useEffect(() => {
    // 🔍 DEBUG: Log locale detection
    console.log('[useTeamsLocale] Starting locale detection', {
      teamsContext: !!teamsContext,
      currentLanguage: i18n.language,
      localStorage: localStorage.getItem('requestateam_language'),
    });

    // Vérifier uniquement un choix EXPLICITE de l'utilisateur (pas une détection automatique du navigateur).
    // Le flag USER_LANGUAGE_CHOICE_KEY est écrit par LanguageSelector lors d'un choix manuel.
    const isUserChoice = localStorage.getItem(USER_LANGUAGE_CHOICE_KEY) === 'true';
    const manualOverride = localStorage.getItem('requestateam_language');
    if (isUserChoice && manualOverride && SUPPORTED_LANGUAGES.includes(manualOverride)) {
      console.log('[useTeamsLocale] Using user explicit language choice:', manualOverride);
      i18n.changeLanguage(manualOverride);
      return;
    }

    if (!teamsContext) {
      console.log('[useTeamsLocale] No Teams context available');
      return;
    }

    const teamsLocale = teamsContext.app?.locale || (teamsContext as any).locale;
    console.log('[useTeamsLocale] Teams locale detected:', teamsLocale);
    
    if (!teamsLocale) {
      console.log('[useTeamsLocale] No Teams locale found');
      return;
    }

    // Teams sometimes sends locale in non-standard casing (e.g. "zh-cn" instead of "zh-CN").
    // Perform a case-insensitive lookup by normalising both sides to lowercase.
    const normalise = (s: string) => s.toLowerCase();
    const mappedLang =
      TEAMS_TO_I18N_MAP[teamsLocale]
      || TEAMS_TO_I18N_MAP[
           Object.keys(TEAMS_TO_I18N_MAP).find(k => normalise(k) === normalise(teamsLocale)) ?? ''
         ]
      || TEAMS_TO_I18N_MAP[teamsLocale.split('-')[0]]
      || TEAMS_TO_I18N_MAP[
           Object.keys(TEAMS_TO_I18N_MAP).find(k => normalise(k) === normalise(teamsLocale.split('-')[0])) ?? ''
         ]
      || DEFAULT_LANGUAGE;

    console.log('[useTeamsLocale] Mapped language:', mappedLang, {
      from: teamsLocale,
      mapping: TEAMS_TO_I18N_MAP[teamsLocale],
    });

    i18n.changeLanguage(mappedLang);
  }, [teamsContext, i18n]);
}
